import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Loader from '@components/Loader';
import NoDataFound from '@components/NoDataFound';
import { isActiveOnlineOrderStatus } from '@src/types/onlineOrder';
import { useNotifications } from '@notifications';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import { ORDER_FILTER_TABS, type OrderFilterTab } from './constants';
import OnlineOrderCard from './OnlineOrderCard';
import './OnlineOrders.scss';

function OnlineOrdersPage({ selectedRestId }: { selectedRestId: string }) {
  const params = useParams();
  const restaurantId = params?.restaurantId || selectedRestId;
  const { onlineOrders, ordersLoading, watchRestaurant } = useNotifications();
  const [filter, setFilter] = useState<OrderFilterTab>('active');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (restaurantId) {
      watchRestaurant(restaurantId);
    }
  }, [restaurantId, watchRestaurant]);

  const filteredOrders = useMemo(() => {
    switch (filter) {
      case 'active':
        return onlineOrders.filter((o) => isActiveOnlineOrderStatus(o.status));
      case 'delivered':
        return onlineOrders.filter((o) => o.status === 'delivered');
      case 'cancelled':
        return onlineOrders.filter((o) => o.status === 'cancelled');
      default:
        return onlineOrders;
    }
  }, [onlineOrders, filter]);

  const stats = useMemo(() => ({
    incoming: onlineOrders.filter((o) => o.status === 'placed').length,
    active: onlineOrders.filter((o) => isActiveOnlineOrderStatus(o.status)).length,
    delivered: onlineOrders.filter((o) => o.status === 'delivered').length,
  }), [onlineOrders]);

  const handleUpdated = useCallback(() => {
    setRefreshKey((k) => k + 1);
  }, []);

  return (
    <div className="online-orders-page">
      <header className="online-orders-page__hero">
        <div className="online-orders-page__hero-text">
          <Typography className="online-orders-page__title" variant="h5">
            Online Orders
          </Typography>
          <Typography className="online-orders-page__subtitle" variant="body2">
            Real-time orders from your digital menu
          </Typography>
        </div>
        <div className="online-orders-page__stats">
          <div className={`online-orders-stat${stats.incoming > 0 ? ' online-orders-stat--alert' : ''}`}>
            <InboxOutlinedIcon />
            <div>
              <span className="online-orders-stat__value">{stats.incoming}</span>
              <span className="online-orders-stat__label">Incoming</span>
            </div>
          </div>
          <div className="online-orders-stat">
            <ShoppingBagOutlinedIcon />
            <div>
              <span className="online-orders-stat__value">{stats.active}</span>
              <span className="online-orders-stat__label">Active</span>
            </div>
          </div>
          <div className="online-orders-stat">
            <CheckCircleOutlineIcon />
            <div>
              <span className="online-orders-stat__value">{stats.delivered}</span>
              <span className="online-orders-stat__label">Delivered</span>
            </div>
          </div>
        </div>
      </header>

      <Box className="online-orders-page__filters" role="tablist" aria-label="Filter orders">
        {ORDER_FILTER_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={filter === tab.key}
            className={`online-orders-filter${filter === tab.key ? ' online-orders-filter--active' : ''}`}
            onClick={() => setFilter(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </Box>

      {ordersLoading && (
        <div className="online-orders-page__loading">
          <Loader />
        </div>
      )}

      {!ordersLoading && filteredOrders.length === 0 && (
        <NoDataFound message="No online orders found for this filter." />
      )}

      {!ordersLoading && filteredOrders.length > 0 && (
        <div className="online-orders-list">
          {filteredOrders.map((order) => (
            <OnlineOrderCard
              key={`${order.id}-${refreshKey}`}
              order={order}
              onUpdated={handleUpdated}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const mapStateToProps = (state: any) => ({
  selectedRestId: state.common?.selectedRestaurant?.docId || '',
});

export default connect(mapStateToProps)(OnlineOrdersPage);
